import { createContext, useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Columns3 } from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "@/lib/api";

const MAX = 4;
const CompareCtx = createContext(null);
export const useCompare = () => useContext(CompareCtx);

export function CompareProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sp-compare") || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem("sp-compare", JSON.stringify(items)); }, [items]);
  const has = (slug) => items.some((i) => i.slug === slug);
  const toggle = (s) => {
    if (has(s.slug)) return setItems(items.filter((i) => i.slug !== s.slug));
    if (items.length >= MAX) return toast.error(`You can compare up to ${MAX} tools at once.`);
    setItems([...items, { slug: s.slug, name: s.name, logo_url: s.logo_url }]);
  };
  const clear = () => setItems([]);
  return <CompareCtx.Provider value={{ items, has, toggle, clear }}>{children}</CompareCtx.Provider>;
}

export function CompareTray() {
  const { items, toggle, clear } = useCompare();
  const { pathname } = useLocation();
  if (!items.length || pathname.startsWith("/compare")) return null;
  return (
    <>
    <div className="h-44 sm:h-24" aria-hidden="true" />
    <div data-testid="compare-tray" className="fixed bottom-4 inset-x-0 z-40 px-4 rise">
      <div className="mx-auto max-w-3xl card shadow-xl border-[#CBD6DB] px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 backdrop-blur bg-white/95">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#17243A] shrink-0">
          <Columns3 className="h-4 w-4 text-[#3F5E7A]" /> Compare ({items.length}/{MAX})
        </div>
        <div className="flex flex-wrap gap-2 flex-1">
          {items.map((i) => (
            <span key={i.slug} className="chip gap-1.5 bg-[#EDF2F4] text-[#17243A]">
              <img src={fileUrl(i.logo_url)} alt="" className="h-4 w-4 rounded" />
              {i.name}
              <button data-testid={`compare-tray-remove-${i.slug}`} aria-label={`Remove ${i.name}`} onClick={() => toggle(i)} className="hover:text-rose-600">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 shrink-0">
          <button data-testid="compare-tray-clear" onClick={clear} className="btn-ghost btn-sm">Clear</button>
          {items.length >= 2 ? (
            <Link data-testid="compare-tray-go" to={`/compare?items=${items.map((i) => i.slug).join(",")}`} className="btn-primary btn-sm">Compare now</Link>
          ) : (
            <span className="text-xs text-[#64728A] self-center">Add one more to compare</span>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
